import type { ReactElement, ReactNode } from 'react'

import { cn } from '@/lib/utils'

import {
  ComingSoonArt,
  ErrorArt,
  ForbiddenArt,
  NotFoundArt,
  type StatusTone,
} from './status-art'

export type StatusVariant = 'not-found' | 'error' | 'forbidden' | 'coming-soon'

const VARIANTS: Record<StatusVariant, { tone: StatusTone; eyebrow: string; Art: () => ReactElement }> = {
  'not-found': { tone: 'primary', eyebrow: 'Hata 404', Art: NotFoundArt },
  error: { tone: 'destructive', eyebrow: 'Beklenmeyen hata', Art: ErrorArt },
  forbidden: { tone: 'accent', eyebrow: 'Erişim yok', Art: ForbiddenArt },
  'coming-soon': { tone: 'primary', eyebrow: 'Yakında', Art: ComingSoonArt },
}

const DOT: Record<StatusTone, string> = {
  primary: 'bg-primary',
  destructive: 'bg-destructive',
  accent: 'bg-accent',
}

// Hareket yalnız "hareketi azalt" kapalıyken oynar; ilk görünüm 250 ms ease-out.
const MOTION_CSS = `
@media (prefers-reduced-motion: no-preference) {
  .ss-in { animation: ss-in 250ms ease-out both; }
  .ss-in-2 { animation: ss-in 250ms ease-out 70ms both; }
  .ss-in-3 { animation: ss-in 250ms ease-out 140ms both; }
  .ss-sweep { transform-origin: 160px 110px; animation: ss-spin 7s linear infinite; }
  .ss-orbit { transform-origin: 160px 110px; animation: ss-spin 16s linear infinite; }
  .ss-float { animation: ss-float 5s ease-in-out infinite; }
  .ss-float-2 { animation: ss-float 6.5s ease-in-out 0.8s infinite; }
  .ss-shake-a { animation: ss-shake-a 4.5s ease-in-out infinite; }
  .ss-shake-b { animation: ss-shake-b 4.5s ease-in-out infinite; }
  .ss-pulse { animation: ss-pulse 2.6s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
  .ss-drop { animation: ss-drop 3.6s ease-in-out infinite; }
}
@keyframes ss-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes ss-spin { to { transform: rotate(360deg); } }
@keyframes ss-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes ss-shake-a { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-3px,2px); } }
@keyframes ss-shake-b { 0%,100% { transform: translate(0,0); } 50% { transform: translate(4px,-3px); } }
@keyframes ss-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.78; transform: scale(1.06); } }
@keyframes ss-drop { 0%,100% { transform: translateY(-5px); opacity: 0.85; } 50% { transform: translateY(2px); opacity: 1; } }
`

interface StatusScreenProps {
  variant: StatusVariant
  title: string
  description: string
  /** Küçük bilgi kapsülü (ör. yanlış adres). */
  detail?: string
  /** Düğmeler / bağlar. */
  actions?: ReactNode
  /** Düğmelerin altı (ör. "Bu sorunu bildir"). */
  footer?: ReactNode
  className?: string
}

export function StatusScreen({ variant, title, description, detail, actions, footer, className }: StatusScreenProps) {
  const { tone, eyebrow, Art } = VARIANTS[variant]
  return (
    <section
      data-testid={`status-screen-${variant}`}
      className={cn('mx-auto flex min-h-[70vh] w-full max-w-xl flex-col items-center justify-center px-6 py-12 text-center', className)}
    >
      <style>{MOTION_CSS}</style>
      <div className="ss-in w-full">
        <Art />
      </div>
      <div className="ss-in-2 mt-2 flex flex-col items-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-secondary/70 px-3 py-1 text-xs font-medium tracking-wide text-foreground/80">
          <span className={cn('size-1.5 rounded-full', DOT[tone])} aria-hidden="true" />
          {eyebrow}
        </span>
        <h1 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-[2rem]">{title}</h1>
        <p className="mt-3 max-w-md text-pretty text-[0.95rem] leading-relaxed text-foreground/70">{description}</p>
        {detail ? (
          <code className="mt-4 max-w-full truncate rounded-md border border-border bg-surface-secondary/60 px-2.5 py-1 font-mono text-xs text-foreground/70">
            {detail}
          </code>
        ) : null}
      </div>
      {actions ? <div className="ss-in-3 mt-7 flex flex-wrap items-center justify-center gap-3">{actions}</div> : null}
      {footer ? <div className="ss-in-3 mt-3">{footer}</div> : null}
    </section>
  )
}
